import { Fragment, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Search, AlertTriangle, CheckCircle2, Truck, Clock, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const VERDICT_STYLE = {
  ja_expedida: "bg-red-100 text-red-800 border-red-200",
  parcial_em_romaneio: "bg-amber-100 text-amber-800 border-amber-200",
  cliente_divergente: "bg-orange-100 text-orange-800 border-orange-200",
  nao_concluida: "bg-slate-100 text-slate-800 border-slate-200",
  disponivel: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const STATUS_LABEL = { pendente: "Pendente", em_andamento: "Em andamento", concluido: "Concluido" };

const ACTION_LABEL = {
  order_status: "Status da ordem",
  stage_status: "Status da etapa",
  sub_stage_status: "Status da sub-etapa",
  expedition_create: "Romaneio criado",
  expedition_update: "Romaneio editado",
  expedition_status: "Status do romaneio",
  expedition_delete: "Romaneio excluido",
};

function OrderTrace({ o }) {
  return (
    <Card className="border-l-4" style={{ borderLeftColor: o.verdict === "disponivel" ? "#10b981" : "#f59e0b" }} data-testid={`trace-order-${o.order_code || o.id}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold break-words">{o.order_code || "(sem codigo)"}</CardTitle>
            <p className="text-xs text-muted-foreground break-words mt-0.5">{o.title}</p>
          </div>
          <Badge className={`${VERDICT_STYLE[o.verdict] || ""} border text-xs whitespace-normal text-left`} data-testid="trace-verdict">
            {o.verdict_label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><p className="text-[11px] uppercase text-muted-foreground">Status</p><p className="font-medium">{STATUS_LABEL[o.status] || o.status}</p></div>
          <div><p className="text-[11px] uppercase text-muted-foreground">Quantidade</p><p className="font-medium">{o.quantity}</p></div>
          <div><p className="text-[11px] uppercase text-muted-foreground">Ja expedida</p><p className="font-medium">{o.expedited_quantity}</p></div>
          <div><p className="text-[11px] uppercase text-muted-foreground">Saldo disponivel</p><p className={`font-semibold ${o.available_quantity > 0 ? "text-emerald-700" : "text-red-700"}`}>{o.available_quantity}</p></div>
          <div className="col-span-2"><p className="text-[11px] uppercase text-muted-foreground">Cliente vinculado</p><p className="font-medium break-words">{o.client_name || "-"} {o.client_code ? `(${o.client_code})` : ""}</p></div>
          <div><p className="text-[11px] uppercase text-muted-foreground">Concluida em</p><p className="font-medium flex items-center gap-1"><Clock className="h-3 w-3" />{fmt(o.concluded_at)}</p></div>
          <div><p className="text-[11px] uppercase text-muted-foreground">Ultima alteracao</p><p className="font-medium">{fmt(o.updated_at)}</p></div>
        </div>

        {o.similar_clients?.length > 1 && (
          <div className="rounded border border-orange-200 bg-orange-50 p-3" data-testid="trace-similar-clients">
            <p className="text-xs font-semibold text-orange-900 flex items-center gap-1 mb-1"><AlertTriangle className="h-3.5 w-3.5" />Cadastros de cliente semelhantes ({o.similar_clients.length})</p>
            <ul className="text-xs text-orange-900 space-y-0.5">
              {o.similar_clients.map((c) => (
                <li key={c.id}>{c.is_order_client ? "-> " : "   "}{c.name} {c.client_code ? `(${c.client_code})` : ""}{c.is_order_client ? " [cliente desta ordem]" : ""}</li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><Truck className="h-3.5 w-3.5" />Romaneios que contem esta ordem</p>
          {o.expeditions?.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border" data-testid="trace-expeditions-table">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2">Romaneio</th><th className="text-left p-2">Qtd</th>
                    <th className="text-left p-2">Status</th><th className="text-left p-2">Criado em</th>
                    <th className="text-left p-2">Criado por</th><th className="text-left p-2">Expedido em</th>
                  </tr>
                </thead>
                <tbody>
                  {o.expeditions.map((e) => (
                    <tr key={e.expedition_id} className="border-t">
                      <td className="p-2 font-medium">{e.expedition_code}</td>
                      <td className="p-2">{e.quantity}</td>
                      <td className="p-2">{e.status}</td>
                      <td className="p-2">{fmt(e.created_at)}</td>
                      <td className="p-2">{e.created_by || "-"}</td>
                      <td className="p-2">{e.expedited_at ? `${fmt(e.expedited_at)} (${e.expedited_by || "-"})` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Nenhum romaneio contem esta ordem.</p>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Etapas e sub-etapas</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border" data-testid="trace-stages-table">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2">Etapa / Sub-etapa</th><th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Iniciada</th><th className="text-left p-2">Concluida</th>
                  <th className="text-left p-2">Por</th>
                </tr>
              </thead>
              <tbody>
                {o.stages?.map((s, i) => (
                  <Fragment key={`s-${i}`}>
                    <tr className="border-t bg-muted/20">
                      <td className="p-2 font-medium">{s.name}</td>
                      <td className="p-2">{STATUS_LABEL[s.status] || s.status}</td>
                      <td className="p-2">{fmt(s.started_at)}</td>
                      <td className="p-2">{fmt(s.completed_at)}</td>
                      <td className="p-2">{s.completed_by || s.assigned_to || "-"}</td>
                    </tr>
                    {s.sub_stages?.map((ss, j) => (
                      <tr key={`ss-${i}-${j}`} className="border-t">
                        <td className="p-2 pl-6 text-muted-foreground">{ss.name}</td>
                        <td className="p-2">{STATUS_LABEL[ss.status] || ss.status}</td>
                        <td className="p-2">{fmt(ss.started_at)}</td>
                        <td className="p-2">{fmt(ss.completed_at)}</td>
                        <td className="p-2">{ss.completed_by || "-"}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" />Log de auditoria ({o.audit_logs?.length || 0})</p>
          {o.audit_logs?.length ? (
            <div className="max-h-56 overflow-y-auto border rounded" data-testid="trace-audit-table">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr><th className="text-left p-2">Data/Hora</th><th className="text-left p-2">Usuario</th><th className="text-left p-2">Acao</th><th className="text-left p-2">Detalhe</th></tr>
                </thead>
                <tbody>
                  {o.audit_logs.map((l) => (
                    <tr key={l.id} className="border-t">
                      <td className="p-2 whitespace-nowrap">{fmt(l.created_at)}</td>
                      <td className="p-2">{l.user_name || l.username}</td>
                      <td className="p-2">{ACTION_LABEL[l.action] || l.action}</td>
                      <td className="p-2 break-words">{l.entity_label ? `${l.entity_label} — ` : ""}{l.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Sem registros de auditoria para esta ordem (o log passou a ser gravado a partir desta versao).</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AuditPage() {
  const [codes, setCodes] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const runTrace = async () => {
    if (!codes.trim()) { toast.error("Informe ao menos um codigo de ordem"); return; }
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/diagnostics/order-trace`, {
        params: { codes: codes.trim() }, withCredentials: true,
      });
      setData(res);
      const total = res.results.reduce((a, r) => a + (r.orders?.length || 0), 0);
      toast.success(`${total} ordem(ns) analisada(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao executar a auditoria");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Auditoria / Rastreamento de Ordens</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cole os codigos (ou parte do codigo/titulo) das ordens, separados por virgula ou uma por linha, para cruzar status, conclusao, cliente, romaneios e log de auditoria.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-3">
          <Textarea
            value={codes}
            onChange={(e) => setCodes(e.target.value)}
            placeholder={"D 35\nD 36\nD 37"}
            rows={4}
            data-testid="audit-codes-input"
          />
          <div className="flex gap-2">
            <Button onClick={runTrace} disabled={loading} data-testid="audit-run-btn">
              <Search className="h-4 w-4 mr-2" />
              {loading ? "Analisando..." : "Rastrear ordens"}
            </Button>
            <Button variant="outline" onClick={() => { setCodes(""); setData(null); }} data-testid="audit-clear-btn">Limpar</Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <div className="space-y-5" data-testid="audit-results">
          <p className="text-xs text-muted-foreground">Consulta executada em {fmt(data.checked_at)} por {data.checked_by}</p>
          {data.results.map((r) => (
            <div key={r.query} className="space-y-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                {r.found ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                Busca: "{r.query}" — {r.found ? `${r.orders.length} ordem(ns)` : "nenhuma ordem encontrada"}
              </h2>
              {r.orders.map((o) => <OrderTrace key={o.id} o={o} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
